import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { track as analytics } from "../lib/analytics";
import { variantsSnapshot } from "../hooks/useExperiment";
import { EXPERIMENTS } from "../lib/experiments";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          analytics.purchaseSuccess({
            session_id: sessionId,
            product_id: data.metadata?.product_id,
            product_name: data.metadata?.product_name,
            amount_total: data.amount_total,
            currency: data.currency,
            mode: data.metadata?.mode,
            variants: variantsSnapshot([
              EXPERIMENTS.SUBSCRIBE_BANNER,
              EXPERIMENTS.LANDING_HEADLINE,
              EXPERIMENTS.LANDING_CTA,
              EXPERIMENTS.RESULT_URGENCY_TONE,
              EXPERIMENTS.WHATSAPP_STYLE,
            ]),
          });
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
        } else {
          setTimeout(poll, POLL_MS);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12" data-testid="checkout-success-page">
      <Card className="w-full max-w-lg rounded-2xl border-emerald-100">
        <CardContent className="p-8 text-center">
          {status === "polling" && (
            <div data-testid="checkout-polling">
              <Loader2 className="w-12 h-12 mx-auto text-emerald-700 animate-spin" />
              <h1 className="font-display text-2xl text-emerald-950 mt-4">Confirming your payment…</h1>
              <p className="text-sm text-emerald-900/70 mt-2">This usually takes a few seconds.</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="checkout-paid">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mx-auto">
                <CheckCircle2 className="w-10 h-10 text-emerald-700" />
              </span>
              <h1 className="font-display text-3xl text-emerald-950 mt-5">You're all set!</h1>
              <p className="text-emerald-900/70 mt-2">
                Your order is confirmed. We've also updated your account with your purchase.
              </p>
              {details ? (
                <div className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900 inline-block text-left">
                  <div><span className="text-emerald-700">Total:</span> {(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}</div>
                  {details.metadata?.product_name ? (
                    <div><span className="text-emerald-700">Product:</span> {details.metadata.product_name}</div>
                  ) : null}
                  {details.metadata?.mode === "subscribe" ? (
                    <div className="text-amber-700 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Subscription saver applied
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-6 flex gap-3 justify-center flex-wrap">
                <Link to="/dashboard">
                  <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="success-go-dashboard">
                    Go to dashboard
                  </Button>
                </Link>
                <Link to="/report">
                  <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50" data-testid="success-go-report">
                    View my plan
                  </Button>
                </Link>
              </div>
              <div className="mt-6 text-[11px] text-emerald-900/60">
                Need help? Email{" "}
                <a className="text-emerald-700 font-semibold hover:underline" href="mailto:support@feelgreats.co.uk">
                  support@feelgreats.co.uk
                </a>{" "}
                · See our{" "}
                <Link to="/refund" className="underline hover:text-emerald-700" data-testid="success-refund-link">Refund Policy</Link>{" "}
                ·{" "}
                <Link to="/terms" className="underline hover:text-emerald-700" data-testid="success-terms-link">Terms</Link>
              </div>
            </div>
          )}
          {status === "expired" && (
            <div data-testid="checkout-expired">
              <XCircle className="w-12 h-12 mx-auto text-amber-600" />
              <h1 className="font-display text-2xl text-emerald-950 mt-4">Session expired</h1>
              <p className="text-sm text-emerald-900/70 mt-2">No charge was made. You can try again.</p>
              <Button onClick={() => navigate("/report")} className="mt-5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">
                Back to my plan
              </Button>
            </div>
          )}
          {status === "error" && (
            <div data-testid="checkout-error">
              <XCircle className="w-12 h-12 mx-auto text-red-600" />
              <h1 className="font-display text-2xl text-emerald-950 mt-4">Couldn't confirm right away</h1>
              <p className="text-sm text-emerald-900/70 mt-2">
                Stripe is processing your payment. Please check back in a few minutes — you'll get a
                confirmation email if it succeeds.
              </p>
              <Button onClick={() => navigate("/dashboard")} className="mt-5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">
                Go to dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
